import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../api/client';
import { toast } from 'sonner';
import {
  Truck,
  Warehouse,
  Calculator,
  Ship,
  BarChart3,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  DollarSign,
  Package,
  MapPin,
  Phone,
  Globe,
  Award,
  TrendingUp,
  TrendingDown,
  ClipboardList
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';

// Import Shipment Planner Tab
import ShipmentPlannerTab from './ShipmentPlannerTab';

const TPLModule = ({ tenantId = 'tenant-123' }) => {
  const [activeTab, setActiveTab] = useState('providers');
  const [providers, setProviders] = useState([]);
  const [products, setProducts] = useState([]);
  const [shippingScenarios, setShippingScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [editingProvider, setEditingProvider] = useState(null);
  
  // Estimation inputs with defaults from spec
  const [monthlyOrders, setMonthlyOrders] = useState(150);
  const [palletsStored, setPalletsStored] = useState(5);
  const [avgItemsPerOrder, setAvgItemsPerOrder] = useState(2);
  const [returnRatePct, setReturnRatePct] = useState(3);
  const [receivingHours, setReceivingHours] = useState(2);
  
  // Fulex estimation state
  const [fulexEstimate, setFulexEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    loadData();
  }, []);

  // Fetch Fulex estimate when inputs change
  useEffect(() => {
    if (providers.length > 0) {
      fetchFulexEstimate();
    }
  }, [monthlyOrders, palletsStored, avgItemsPerOrder, returnRatePct, receivingHours, providers]);

  const fetchFulexEstimate = async () => {
    setEstimateLoading(true);
    try {
      const params = new URLSearchParams({
        monthly_orders: monthlyOrders,
        pallets_stored: palletsStored,
        avg_items_per_order: avgItemsPerOrder,
        return_rate_pct: returnRatePct,
        receiving_hours: receivingHours,
        tenant_id: tenantId
      });
      const res = await api.post(`/api/v1/tpl/estimate?${params.toString()}`);
      const data = res.data || res;
      if (data.success) {
        setFulexEstimate(data);
      }
    } catch (err) {
      console.error('Error fetching estimate:', err);
    } finally {
      setEstimateLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // First, seed/update Fulex with complete rate card
      try {
        await api.post(`/api/v1/tpl/seed-fulex?tenant_id=${tenantId}`);
      } catch (seedErr) {
        console.log('Fulex seed:', seedErr.message);
      }

      // Fetch providers
      const providersRes = await api.get(`/api/v1/tpl/providers?tenant_id=${tenantId}`);
      let providerList = providersRes.data?.providers || providersRes.providers || [];
      
      // If no providers, seed them
      if (providerList.length === 0) {
        await api.post(`/api/v1/tpl/seed-providers?tenant_id=${tenantId}`);
        const refreshedRes = await api.get(`/api/v1/tpl/providers?tenant_id=${tenantId}`);
        providerList = refreshedRes.data?.providers || refreshedRes.providers || [];
      }
      setProviders(providerList);

      // Fetch products for unit economics
      const productsRes = await api.get(`/api/v1/tpl/products?tenant_id=${tenantId}`);
      setProducts(productsRes.data?.products || productsRes.products || []);

      // Fetch shipping scenarios
      const shippingRes = await api.get(`/api/v1/tpl/shipping-scenarios?tenant_id=${tenantId}`);
      setShippingScenarios(shippingRes.data?.scenarios || shippingRes.scenarios || []);

    } catch (err) {
      console.error('Error loading 3PL data:', err);
      toast.error('Failed to load 3PL data');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'providers', label: '3PL Providers', icon: Warehouse },
    { id: 'economics', label: 'Unit Economics', icon: Calculator },
    { id: 'shipping', label: 'Shipping Calculator', icon: Ship },
    { id: 'planner', label: 'Shipment Planner', icon: ClipboardList },
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 }
  ];

  // Format currency
  const formatCurrency = (value, showZero = false) => {
    if (!value && value !== 0) return '—';
    if (value === 0 && !showZero) return '—';
    return `$${value.toFixed(2)}`;
  };

  // Status badge colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'selected': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'quoted': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'contacted': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'rejected': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    }
  };

  // Calculate estimated monthly cost for a provider
  const calculateMonthlyCost = (provider) => {
    // For Fulex, use the API estimate if available
    if (provider.provider_name === 'Fulex' && fulexEstimate?.estimate) {
      return fulexEstimate.estimate.total_excluding_shipping;
    }
    
    // For other providers, use simple calculation
    const costs = provider.costs || {};
    const storage = (costs.storage_per_pallet_monthly || 0) * palletsStored;
    const pickPack = (costs.pick_pack_combined || (costs.pick_fee_per_order || 0) + (costs.pack_fee_per_order || 0)) * monthlyOrders;
    const shipping = (costs.shipping_per_order_avg || 0) * monthlyOrders;
    const minimum = costs.minimum_monthly || 0;
    const subtotal = storage + pickPack + shipping;
    return Math.max(subtotal, minimum);
  };

  // Update provider
  const saveProvider = async (provider) => {
    try {
      await api.put(`/api/v1/tpl/providers/${provider.id}?tenant_id=${tenantId}`, provider);
      toast.success('Provider updated');
      setEditingProvider(null);
      loadData();
    } catch (err) {
      toast.error('Failed to update provider');
    }
  };

  // ============ PROVIDERS TAB ============
  const ProvidersTab = () => {
    const [expandedProvider, setExpandedProvider] = useState(null);
    const [editCosts, setEditCosts] = useState({});

    const costCategories = [
      { key: 'storage_per_pallet_monthly', label: 'Storage/Pallet/Mo' },
      { key: 'pick_pack_combined', label: 'Pick & Pack/Order' },
      { key: 'shipping_per_order_avg', label: 'Shipping/Order' },
      { key: 'receiving_per_pallet', label: 'Receiving/Pallet' },
      { key: 'kitting_per_unit', label: 'Kitting/Unit' },
      { key: 'returns_per_order', label: 'Returns/Order' },
      { key: 'minimum_monthly', label: 'Min Monthly' },
      { key: 'setup_fee', label: 'Setup Fee' }
    ];

    const handleCostChange = (providerId, costKey, value) => {
      setEditCosts(prev => ({
        ...prev,
        [providerId]: {
          ...prev[providerId],
          [costKey]: parseFloat(value) || 0
        }
      }));
    };

    const saveCosts = async (provider) => {
      const updatedCosts = { ...provider.costs, ...editCosts[provider.id] };
      try {
        await api.put(`/api/v1/tpl/providers/${provider.id}?tenant_id=${tenantId}`, {
          ...provider,
          costs: updatedCosts
        });
        toast.success('Costs updated');
        setEditCosts(prev => ({ ...prev, [provider.id]: {} }));
        loadData();
      } catch (err) {
        toast.error('Failed to save costs');
      }
    };

    // Find lowest cost per category
    const getLowestForCategory = (costKey) => {
      let lowest = Infinity;
      providers.forEach(p => {
        const val = p.costs?.[costKey] || 0;
        if (val > 0 && val < lowest) lowest = val;
      });
      return lowest === Infinity ? 0 : lowest;
    };

    // Get Fulex cost display value for comparison table
    const getFulexCostDisplay = (costKey) => {
      const fulex = providers.find(p => p.provider_name === 'Fulex');
      if (!fulex) return null;
      
      // For Fulex, show values from the rates structure
      const rates = fulex.rates || {};
      const costs = fulex.costs || {};
      
      switch(costKey) {
        case 'storage_per_pallet_monthly':
          return rates.storage_pallet_per_month || costs.storage_per_pallet_monthly || 20.00;
        case 'pick_pack_combined':
          // Show tier 1 rate
          return rates.order_fee_tiers?.[0]?.rate || costs.pick_pack_combined || 2.40;
        case 'shipping_per_order_avg':
          return 'Client Pays';
        case 'receiving_per_pallet':
          return rates.receiving_per_hour || costs.receiving_per_pallet || 40.00;
        case 'returns_per_order':
          return rates.ecommerce_return || costs.returns_per_order || 3.50;
        case 'minimum_monthly':
          return rates.monthly_minimum_pick_fee || costs.minimum_monthly || 250.00;
        case 'setup_fee':
          return 0;
        default:
          return costs[costKey] || 0;
      }
    };

    return (
      <div className="space-y-4">
        {/* Volume Inputs - Extended with all parameters */}
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <div className="flex items-center gap-2 mb-3">
            <Calculator className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-semibold text-white">Estimation Parameters</span>
          </div>
          <div className="grid grid-cols-5 gap-4">
            <div>
              <label className="text-[10px] text-gray-400 block mb-1">Monthly Orders</label>
              <input
                type="number"
                value={monthlyOrders}
                onChange={(e) => setMonthlyOrders(parseInt(e.target.value) || 0)}
                className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-sm text-white"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-1">Pallets Stored</label>
              <input
                type="number"
                value={palletsStored}
                onChange={(e) => setPalletsStored(parseInt(e.target.value) || 0)}
                className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-sm text-white"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-1">Avg Items/Order</label>
              <input
                type="number"
                step="0.1"
                value={avgItemsPerOrder}
                onChange={(e) => setAvgItemsPerOrder(parseFloat(e.target.value) || 0)}
                className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-sm text-white"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-1">Return Rate %</label>
              <input
                type="number"
                step="0.1"
                value={returnRatePct}
                onChange={(e) => setReturnRatePct(parseFloat(e.target.value) || 0)}
                className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-sm text-white"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-1">Receiving Hrs/Mo</label>
              <input
                type="number"
                step="0.5"
                value={receivingHours}
                onChange={(e) => setReceivingHours(parseFloat(e.target.value) || 0)}
                className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-sm text-white"
              />
            </div>
          </div>
        </div>

        {/* Fulex Estimate Breakdown - Only show when estimate is available */}
        {fulexEstimate?.estimate && (
          <div className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 rounded-lg p-4 border border-emerald-500/30">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white">Fulex Estimate</h3>
                  <p className="text-[10px] text-gray-400">Auto-calculated from rate card</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-emerald-400">
                  ${fulexEstimate.estimate.total_excluding_shipping.toFixed(2)}
                </div>
                <div className="text-[10px] text-gray-400">Est. Monthly (excl. shipping)</div>
              </div>
            </div>
            
            <div className="grid grid-cols-6 gap-3 mt-4">
              <div className="bg-slate-800/50 rounded-lg p-2">
                <div className="text-[10px] text-gray-400">Order Fee</div>
                <div className="text-sm font-semibold text-white">${fulexEstimate.estimate.order_fee.toFixed(2)}</div>
                <div className="text-[9px] text-cyan-400">{fulexEstimate.estimate.order_tier_label}</div>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-2">
                <div className="text-[10px] text-gray-400">Item Fee</div>
                <div className="text-sm font-semibold text-white">${fulexEstimate.estimate.item_fee.toFixed(2)}</div>
                <div className="text-[9px] text-cyan-400">{fulexEstimate.estimate.item_tier_label}</div>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-2">
                <div className="text-[10px] text-gray-400">Storage</div>
                <div className="text-sm font-semibold text-white">${fulexEstimate.estimate.storage_fee.toFixed(2)}</div>
                <div className="text-[9px] text-cyan-400">{palletsStored} pallets × $20</div>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-2">
                <div className="text-[10px] text-gray-400">Returns</div>
                <div className="text-sm font-semibold text-white">${fulexEstimate.estimate.return_fee.toFixed(2)}</div>
                <div className="text-[9px] text-cyan-400">{fulexEstimate.estimate.num_returns} × $3.50</div>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-2">
                <div className="text-[10px] text-gray-400">Receiving</div>
                <div className="text-sm font-semibold text-white">${fulexEstimate.estimate.receiving_fee.toFixed(2)}</div>
                <div className="text-[9px] text-cyan-400">{receivingHours}hr × $40</div>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-2">
                <div className="text-[10px] text-gray-400">Shipping</div>
                <div className="text-sm font-semibold text-orange-400">Client Pays</div>
                <div className="text-[9px] text-gray-500">Direct to carrier</div>
              </div>
            </div>
            
            {fulexEstimate.estimate.minimum_applied && (
              <div className="mt-3 px-3 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-xs text-yellow-400">
                ⚠️ Monthly minimum of ${fulexEstimate.estimate.minimum_amount} applied (pick fees below threshold)
              </div>
            )}
          </div>
        )}

        {/* Provider Cards */}
        <div className="grid grid-cols-5 gap-3">
          {providers.map(provider => {
            const isFulex = provider.provider_name === 'Fulex';
            const monthlyCost = isFulex && fulexEstimate?.estimate 
              ? fulexEstimate.estimate.total_excluding_shipping 
              : calculateMonthlyCost(provider);
            const hasCosts = monthlyCost > 0;
            
            return (
              <div
                key={provider.id}
                data-testid={`provider-card-${provider.provider_name?.toLowerCase().replace(/\s+/g, '-')}`}
                className={`bg-slate-800 rounded-lg p-3 border border-slate-700 hover:border-slate-600 cursor-pointer transition-all ${
                  expandedProvider === provider.id ? 'ring-1 ring-blue-500' : ''
                } ${isFulex && hasCosts ? 'ring-1 ring-emerald-500/50' : ''}`}
                onClick={() => setExpandedProvider(expandedProvider === provider.id ? null : provider.id)}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="text-sm font-semibold text-white truncate">{provider.provider_name}</h4>
                  {provider.fda_compliant && (
                    <span className="flex-shrink-0 px-1.5 py-0.5 bg-green-500/20 text-green-400 text-[9px] rounded border border-green-500/30">
                      FDA
                    </span>
                  )}
                </div>
                
                <div className="flex items-center gap-1 text-[10px] text-gray-400 mb-2">
                  <MapPin className="w-3 h-3" />
                  {provider.location}
                </div>
                
                <div className={`inline-flex px-1.5 py-0.5 text-[9px] rounded border ${getStatusColor(provider.status)}`}>
                  {provider.status}
                </div>
                
                <div className="mt-2 pt-2 border-t border-slate-700">
                  <div className="text-[10px] text-gray-500">Est. Monthly</div>
                  <div className={`text-sm font-semibold ${hasCosts ? (isFulex ? 'text-emerald-400' : 'text-emerald-400') : 'text-gray-600'}`}>
                    {hasCosts ? `$${monthlyCost.toFixed(2)}` : '—'}
                  </div>
                  {isFulex && hasCosts && (
                    <div className="text-[9px] text-cyan-400 mt-0.5">✓ Rate card loaded</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Comparison Table */}
        <div className="bg-slate-800 rounded-lg overflow-hidden">
          <div className="p-3 border-b border-slate-700">
            <h3 className="text-sm font-semibold text-white">Side-by-Side Cost Comparison</h3>
            <p className="text-[10px] text-gray-400">Click any cell to edit • Lowest costs highlighted in green</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-900">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-400 font-medium w-[140px]">Cost Category</th>
                  {providers.map(p => (
                    <th key={p.id} className="px-3 py-2 text-right text-[10px] uppercase text-gray-400 font-medium min-w-[120px]">
                      {p.provider_name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {costCategories.map((cat, idx) => {
                  const lowest = getLowestForCategory(cat.key);
                  return (
                    <tr key={cat.key} className={`border-b border-slate-700 ${idx % 2 === 0 ? 'bg-slate-800/50' : ''}`}>
                      <td className="px-3 py-2 text-gray-300">{cat.label}</td>
                      {providers.map(p => {
                        const isFulex = p.provider_name === 'Fulex';
                        const originalVal = p.costs?.[cat.key] || 0;
                        const editedVal = editCosts[p.id]?.[cat.key];
                        
                        // For Fulex, use the rate card values
                        let displayVal;
                        let displayText = null;
                        if (isFulex) {
                          const fulexVal = getFulexCostDisplay(cat.key);
                          if (typeof fulexVal === 'string') {
                            displayText = fulexVal;
                            displayVal = 0;
                          } else {
                            displayVal = fulexVal;
                          }
                        } else {
                          displayVal = editedVal !== undefined ? editedVal : originalVal;
                        }
                        
                        const isLowest = displayVal > 0 && displayVal === lowest;
                        const isEdited = editedVal !== undefined && editedVal !== originalVal;
                        
                        return (
                          <td key={p.id} className="px-3 py-2 text-right">
                            {displayText ? (
                              <span className="text-orange-400 text-xs">{displayText}</span>
                            ) : isFulex && cat.key !== 'setup_fee' ? (
                              <span className={`text-xs ${isLowest ? 'text-green-400 font-semibold' : 'text-cyan-400'}`}>
                                ${displayVal.toFixed(2)}
                              </span>
                            ) : (
                              <input
                                type="number"
                                value={displayVal || ''}
                                onChange={(e) => handleCostChange(p.id, cat.key, e.target.value)}
                                placeholder="—"
                                className={`w-full bg-transparent border-b border-transparent hover:border-slate-600 focus:border-blue-500 focus:outline-none text-right px-1 py-0.5 rounded ${
                                  isLowest ? 'text-green-400 font-semibold' : 'text-gray-300'
                                } ${isEdited ? 'bg-blue-900/30' : ''}`}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                
                {/* Estimated Monthly Total Row */}
                <tr className="bg-slate-900 font-semibold border-t-2 border-slate-600">
                  <td className="px-3 py-2 text-white">Est. Monthly Total</td>
                  {providers.map(p => {
                    const isFulex = p.provider_name === 'Fulex';
                    let cost;
                    
                    if (isFulex && fulexEstimate?.estimate) {
                      cost = fulexEstimate.estimate.total_excluding_shipping;
                    } else {
                      cost = calculateMonthlyCost({
                        ...p,
                        costs: { ...p.costs, ...editCosts[p.id] }
                      });
                    }
                    
                    const allCosts = providers.map(pr => {
                      if (pr.provider_name === 'Fulex' && fulexEstimate?.estimate) {
                        return fulexEstimate.estimate.total_excluding_shipping;
                      }
                      return calculateMonthlyCost({
                        ...pr,
                        costs: { ...pr.costs, ...editCosts[pr.id] }
                      });
                    }).filter(c => c > 0);
                    const isLowest = cost > 0 && cost === Math.min(...allCosts);
                    
                    return (
                      <td key={p.id} className={`px-3 py-2 text-right ${
                        cost > 0 ? (isLowest ? 'text-green-400' : 'text-white') : 'text-gray-600'
                      }`}>
                        {cost > 0 ? (
                          <span className={isFulex ? 'text-emerald-400 font-bold' : ''}>
                            ${cost.toFixed(2)}
                          </span>
                        ) : '—'}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Save buttons if any edits */}
          {Object.keys(editCosts).some(id => Object.keys(editCosts[id]).length > 0) && (
            <div className="p-3 border-t border-slate-700 flex justify-end gap-2">
              <button
                onClick={() => setEditCosts({})}
                className="px-3 py-1.5 text-xs text-gray-400 hover:text-white bg-slate-700 rounded"
              >
                Discard All
              </button>
              <button
                onClick={() => {
                  providers.forEach(p => {
                    if (editCosts[p.id] && Object.keys(editCosts[p.id]).length > 0) {
                      saveCosts(p);
                    }
                  });
                }}
                className="px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-500 rounded flex items-center gap-1"
              >
                <Save className="w-3 h-3" />
                Save All Changes
              </button>
            </div>
          )}
        </div>

        {/* Provider Details Panel */}
        {expandedProvider && (
          <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
            {(() => {
              const provider = providers.find(p => p.id === expandedProvider);
              if (!provider) return null;
              
              return (
                <>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{provider.provider_name}</h3>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                        {provider.address && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {provider.address}
                          </span>
                        )}
                        {provider.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {provider.phone}
                          </span>
                        )}
                        {provider.website && (
                          <a href={provider.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-400 hover:underline">
                            <Globe className="w-3 h-3" /> Website
                          </a>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {provider.fda_compliant && (
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded border border-green-500/30">
                          FDA Certified
                        </span>
                      )}
                      {provider.organic_certified && (
                        <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded border border-emerald-500/30">
                          Organic Handler
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">Notes</h4>
                      <p className="text-sm text-gray-300">{provider.notes || 'No notes'}</p>
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">Integrations</h4>
                      <div className="flex flex-wrap gap-1">
                        {(provider.integrations || []).map((int, i) => (
                          <span key={i} className="px-2 py-0.5 bg-slate-700 text-gray-300 text-xs rounded">
                            {int}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Status Selector */}
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400">Status:</span>
                      <select
                        value={provider.status}
                        onChange={async (e) => {
                          try {
                            await api.put(`/api/v1/tpl/providers/${provider.id}?tenant_id=${tenantId}`, {
                              ...provider,
                              status: e.target.value
                            });
                            loadData();
                            toast.success('Status updated');
                          } catch (err) {
                            toast.error('Failed to update status');
                          }
                        }}
                        className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white"
                      >
                        <option value="evaluating">Evaluating</option>
                        <option value="contacted">Contacted</option>
                        <option value="quoted">Quoted</option>
                        <option value="selected">Selected</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  // ============ UNIT ECONOMICS TAB ============
  const UnitEconomicsTab = () => {
    const [selectedSKU, setSelectedSKU] = useState('');
    const [selectedProviderId, setSelectedProviderId] = useState('');
    const [indiaData, setIndiaData] = useState({
      manufacturing_cost: 0,
      ocean_freight_per_unit: 0,
      customs_duties_per_unit: 0,
      freight_forwarding_per_unit: 0
    });
    const [michiganData, setMichiganData] = useState({
      vendor_cost: 0
    });
    const [tplCosts, setTplCosts] = useState({
      storage_per_unit: 0,
      pick_pack_per_unit: 0,
      shipping_per_unit: 0
    });
    const [sellingPrice, setSellingPrice] = useState(0);

    // Load product data when SKU changes
    useEffect(() => {
      if (selectedSKU) {
        const product = products.find(p => p.sku === selectedSKU);
        if (product) {
          const pricing = product.pricing_v2 || {};
          setSellingPrice(pricing.wholesale_price || 0);
          setMichiganData({ vendor_cost: pricing.total_cost || 0 });
          
          // ========================================================================
          // PERMANENT FIX: Use component_cost for BOM products — do not remove
          // Manufacturing Cost logic:
          // 1. ALWAYS check component_cost FIRST - if > 0, use it (BOM products)
          // 2. Only fall back to raw+labor+overhead if component_cost is 0/missing
          // ========================================================================
          let manufacturingCost = 0;
          
          // PERMANENT FIX: Check component_cost FIRST regardless of costing_mode
          if (product.component_cost && product.component_cost > 0) {
            manufacturingCost = product.component_cost;
          } else {
            // Fallback: Standard product - use raw materials + labor + factory overhead
            manufacturingCost = (pricing.raw_materials_cost || 0) + 
                               (pricing.labor_cost || 0) + 
                               (pricing.factory_overhead || 0);
          }
          
          setIndiaData({
            manufacturing_cost: manufacturingCost,
            ocean_freight_per_unit: pricing.freight_cost || 0,
            customs_duties_per_unit: pricing.duties_cost || 0,
            freight_forwarding_per_unit: pricing.handling_cost || 0
          });
          
          console.log(`📊 Unit Economics: SKU ${selectedSKU}`, {
            costing_mode: product.costing_mode,
            component_cost: product.component_cost,
            manufacturing_cost: manufacturingCost,
            total_cost: pricing.total_cost
          });
        }
      } else {
        // Reset when no product selected
        setIndiaData({
          manufacturing_cost: 0,
          ocean_freight_per_unit: 0,
          customs_duties_per_unit: 0,
          freight_forwarding_per_unit: 0
        });
        setMichiganData({ vendor_cost: 0 });
        setSellingPrice(0);
      }
    }, [selectedSKU, products]);

    // Load provider costs when provider changes
    useEffect(() => {
      if (selectedProviderId) {
        const provider = providers.find(p => p.id === selectedProviderId);
        if (provider) {
          const costs = provider.costs || {};
          const unitsPerOrder = 2;
          setTplCosts({
            storage_per_unit: (costs.storage_per_pallet_monthly || 0) / 100, // Estimate per unit
            pick_pack_per_unit: (costs.pick_pack_combined || 0) / unitsPerOrder,
            shipping_per_unit: (costs.shipping_per_order_avg || 0) / unitsPerOrder
          });
        }
      }
    }, [selectedProviderId, providers]);

    // Calculate totals
    const indiaLandedCost = indiaData.manufacturing_cost + indiaData.ocean_freight_per_unit + 
                           indiaData.customs_duties_per_unit + indiaData.freight_forwarding_per_unit;
    const indiaTotalCost = indiaLandedCost + tplCosts.storage_per_unit + tplCosts.pick_pack_per_unit + tplCosts.shipping_per_unit;
    const indiaMargin = sellingPrice - indiaTotalCost;
    const indiaMarginPct = sellingPrice > 0 ? (indiaMargin / sellingPrice) * 100 : 0;

    const michiganLandedCost = michiganData.vendor_cost;
    const michiganTotalCost = michiganLandedCost + tplCosts.storage_per_unit + tplCosts.pick_pack_per_unit + tplCosts.shipping_per_unit;
    const michiganMargin = sellingPrice - michiganTotalCost;
    const michiganMarginPct = sellingPrice > 0 ? (michiganMargin / sellingPrice) * 100 : 0;

    const savings = michiganTotalCost - indiaTotalCost;
    const savingsPct = michiganTotalCost > 0 ? (savings / michiganTotalCost) * 100 : 0;

    return (
      <div className="space-y-4">
        {/* Controls */}
        <div className="bg-slate-800 rounded-lg p-3 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Product:</span>
            <select
              value={selectedSKU}
              onChange={(e) => setSelectedSKU(e.target.value)}
              className="bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-xs text-white min-w-[250px]"
            >
              <option value="">Select a product...</option>
              {products.map(p => (
                <option key={p.sku} value={p.sku}>{p.sku} — {p.name?.substring(0, 40)}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">3PL Provider:</span>
            <select
              value={selectedProviderId}
              onChange={(e) => setSelectedProviderId(e.target.value)}
              className="bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-xs text-white min-w-[150px]"
            >
              <option value="">Select provider...</option>
              {providers.map(p => (
                <option key={p.id} value={p.id}>{p.provider_name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Two-Column Comparison */}
        <div className="grid grid-cols-2 gap-4">
          {/* India Import Column */}
          <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-xl">🇮🇳</span>
                <h3 className="text-sm font-semibold text-white">INDIA IMPORT</h3>
              </div>
              {selectedSKU && (
                <span className="text-[9px] text-cyan-400 bg-cyan-900/30 px-2 py-0.5 rounded">
                  ✓ From Product Master
                </span>
              )}
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-xs text-gray-400">Manufacturing Cost:</span>
                  {selectedSKU && <span className="text-[9px] text-gray-500">Raw + Labor + Overhead</span>}
                </div>
                <input
                  type="number"
                  step="0.01"
                  value={indiaData.manufacturing_cost || ''}
                  onChange={(e) => setIndiaData({...indiaData, manufacturing_cost: parseFloat(e.target.value) || 0})}
                  className={`w-24 border rounded px-2 py-1 text-xs text-right ${
                    selectedSKU && indiaData.manufacturing_cost > 0 
                      ? 'bg-cyan-900/20 border-cyan-700/50 text-cyan-300' 
                      : 'bg-slate-700 border-slate-600 text-white'
                  }`}
                  placeholder="$0.00"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-xs text-gray-400">Ocean Freight/Unit:</span>
                  {selectedSKU && <span className="text-[9px] text-gray-500">from Product Master</span>}
                </div>
                <input
                  type="number"
                  step="0.01"
                  value={indiaData.ocean_freight_per_unit || ''}
                  onChange={(e) => setIndiaData({...indiaData, ocean_freight_per_unit: parseFloat(e.target.value) || 0})}
                  className={`w-24 border rounded px-2 py-1 text-xs text-right ${
                    selectedSKU && indiaData.ocean_freight_per_unit > 0 
                      ? 'bg-cyan-900/20 border-cyan-700/50 text-cyan-300' 
                      : 'bg-slate-700 border-slate-600 text-white'
                  }`}
                  placeholder="$0.00"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-xs text-gray-400">Customs & Duties:</span>
                  {selectedSKU && <span className="text-[9px] text-gray-500">from Product Master</span>}
                </div>
                <input
                  type="number"
                  step="0.01"
                  value={indiaData.customs_duties_per_unit || ''}
                  onChange={(e) => setIndiaData({...indiaData, customs_duties_per_unit: parseFloat(e.target.value) || 0})}
                  className={`w-24 border rounded px-2 py-1 text-xs text-right ${
                    selectedSKU && indiaData.customs_duties_per_unit > 0 
                      ? 'bg-cyan-900/20 border-cyan-700/50 text-cyan-300' 
                      : 'bg-slate-700 border-slate-600 text-white'
                  }`}
                  placeholder="$0.00"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-xs text-gray-400">Freight Forwarding:</span>
                  {selectedSKU && <span className="text-[9px] text-gray-500">from Product Master</span>}
                </div>
                <input
                  type="number"
                  step="0.01"
                  value={indiaData.freight_forwarding_per_unit || ''}
                  onChange={(e) => setIndiaData({...indiaData, freight_forwarding_per_unit: parseFloat(e.target.value) || 0})}
                  className={`w-24 border rounded px-2 py-1 text-xs text-right ${
                    selectedSKU && indiaData.freight_forwarding_per_unit > 0 
                      ? 'bg-cyan-900/20 border-cyan-700/50 text-cyan-300' 
                      : 'bg-slate-700 border-slate-600 text-white'
                  }`}
                  placeholder="$0.00"
                />
              </div>
              
              <div className="border-t border-slate-700 pt-2">
                <div className="flex items-center justify-between font-semibold">
                  <span className="text-xs text-gray-300">Landed Cost:</span>
                  <span className="text-sm text-white">${indiaLandedCost.toFixed(2)}</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">3PL Storage/Unit:</span>
                <span className="text-xs text-gray-300">${tplCosts.storage_per_unit.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">3PL Pick+Pack/Unit:</span>
                <span className="text-xs text-gray-300">${tplCosts.pick_pack_per_unit.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">3PL Shipping/Unit:</span>
                <span className="text-xs text-gray-300">${tplCosts.shipping_per_unit.toFixed(2)}</span>
              </div>
              
              <div className="border-t border-slate-700 pt-2">
                <div className="flex items-center justify-between font-semibold">
                  <span className="text-xs text-gray-300">Total Cost/Unit:</span>
                  <span className="text-sm text-white">${indiaTotalCost.toFixed(2)}</span>
                </div>
              </div>
              
              <div className="border-t border-slate-700 pt-2 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Selling Price:</span>
                  <input
                    type="number"
                    value={sellingPrice || ''}
                    onChange={(e) => setSellingPrice(parseFloat(e.target.value) || 0)}
                    className="w-24 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white text-right"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-300">Gross Margin:</span>
                  <span className={`text-sm font-semibold ${indiaMargin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    ${indiaMargin.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-300">Margin %:</span>
                  <span className={`text-sm font-semibold ${indiaMarginPct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {indiaMarginPct.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Michigan Vendor Column */}
          <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">🇺🇸</span>
              <h3 className="text-sm font-semibold text-white">MICHIGAN VENDOR</h3>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">Vendor Unit Cost:</span>
                <input
                  type="number"
                  value={michiganData.vendor_cost || ''}
                  onChange={(e) => setMichiganData({...michiganData, vendor_cost: parseFloat(e.target.value) || 0})}
                  className="w-24 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white text-right"
                  placeholder="$0.00"
                />
              </div>
              
              <div className="h-[76px]" /> {/* Spacer to align with India column */}
              
              <div className="border-t border-slate-700 pt-2">
                <div className="flex items-center justify-between font-semibold">
                  <span className="text-xs text-gray-300">Landed Cost:</span>
                  <span className="text-sm text-white">${michiganLandedCost.toFixed(2)}</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">3PL Storage/Unit:</span>
                <span className="text-xs text-gray-300">${tplCosts.storage_per_unit.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">3PL Pick+Pack/Unit:</span>
                <span className="text-xs text-gray-300">${tplCosts.pick_pack_per_unit.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">3PL Shipping/Unit:</span>
                <span className="text-xs text-gray-300">${tplCosts.shipping_per_unit.toFixed(2)}</span>
              </div>
              
              <div className="border-t border-slate-700 pt-2">
                <div className="flex items-center justify-between font-semibold">
                  <span className="text-xs text-gray-300">Total Cost/Unit:</span>
                  <span className="text-sm text-white">${michiganTotalCost.toFixed(2)}</span>
                </div>
              </div>
              
              <div className="border-t border-slate-700 pt-2 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Selling Price:</span>
                  <span className="text-xs text-gray-300">${sellingPrice.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-300">Gross Margin:</span>
                  <span className={`text-sm font-semibold ${michiganMargin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    ${michiganMargin.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-300">Margin %:</span>
                  <span className={`text-sm font-semibold ${michiganMarginPct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {michiganMarginPct.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Savings Summary */}
        {indiaTotalCost > 0 && michiganTotalCost > 0 && (
          <div className={`rounded-lg p-4 ${savings > 0 ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
            <div className="flex items-center justify-center gap-3">
              <DollarSign className={`w-6 h-6 ${savings > 0 ? 'text-green-400' : 'text-red-400'}`} />
              <span className={`text-xl font-bold ${savings > 0 ? 'text-green-400' : 'text-red-400'}`}>
                {savings > 0 ? 'SAVINGS' : 'ADDITIONAL COST'}: ${Math.abs(savings).toFixed(2)} per unit ({Math.abs(savingsPct).toFixed(1)}% {savings > 0 ? 'cheaper' : 'more'})
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============ SHIPPING CALCULATOR TAB ============
  const ShippingCalculatorTab = () => {
    const [inbound, setInbound] = useState({
      container_type: '20ft',
      origin: 'Mumbai, India',
      destination: 'Warren, MI',
      container_cost: 0,
      customs_total: 0,
      duties_total: 0,
      freight_forwarding: 0,
      drayage: 0,
      insurance: 0,
      other_charges: 0,
      units_per_container: 0,
      transit_days: 35
    });

    const [outboundRates, setOutboundRates] = useState([
      { carrier: 'USPS First Class', cost: 0, transit_days: '3-5', notes: 'Light items <1lb' },
      { carrier: 'USPS Priority', cost: 0, transit_days: '2-3', notes: 'Most orders' },
      { carrier: 'UPS Ground', cost: 0, transit_days: '3-5', notes: 'Heavier items' },
      { carrier: 'FedEx Ground', cost: 0, transit_days: '3-5', notes: 'Alternative' },
      { carrier: 'FedEx SmartPost', cost: 0, transit_days: '5-7', notes: 'Economy option' }
    ]);

    // Calculate totals
    const totalInbound = inbound.container_cost + inbound.customs_total + inbound.duties_total + 
                        inbound.freight_forwarding + inbound.drayage + inbound.insurance + inbound.other_charges;
    const costPerUnit = inbound.units_per_container > 0 ? totalInbound / inbound.units_per_container : 0;

    return (
      <div className="space-y-4">
        {/* Inbound Shipping */}
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <Ship className="w-4 h-4 text-blue-400" />
            Inbound Shipping (India → 3PL Warehouse)
          </h3>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-gray-400 block mb-1">Container Type</label>
                <select
                  value={inbound.container_type}
                  onChange={(e) => setInbound({...inbound, container_type: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-xs text-white"
                >
                  <option value="20ft">20ft Container</option>
                  <option value="40ft">40ft Container</option>
                  <option value="40ft_hc">40ft High Cube</option>
                  <option value="lcl">LCL (Less than Container)</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-gray-400 block mb-1">Origin Port</label>
                <input
                  type="text"
                  value={inbound.origin}
                  onChange={(e) => setInbound({...inbound, origin: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-xs text-white"
                />
              </div>
              <div>
                <label className="text-[10px] text-gray-400 block mb-1">Destination</label>
                <input
                  type="text"
                  value={inbound.destination}
                  onChange={(e) => setInbound({...inbound, destination: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-xs text-white"
                />
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-gray-400 block mb-1">Container Cost</label>
                <input
                  type="number"
                  value={inbound.container_cost || ''}
                  onChange={(e) => setInbound({...inbound, container_cost: parseFloat(e.target.value) || 0})}
                  className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-xs text-white"
                  placeholder="$0.00"
                />
              </div>
              <div>
                <label className="text-[10px] text-gray-400 block mb-1">Customs & Import Duties</label>
                <input
                  type="number"
                  value={inbound.customs_total || ''}
                  onChange={(e) => setInbound({...inbound, customs_total: parseFloat(e.target.value) || 0})}
                  className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-xs text-white"
                  placeholder="$0.00"
                />
              </div>
              <div>
                <label className="text-[10px] text-gray-400 block mb-1">Freight Forwarding Fee</label>
                <input
                  type="number"
                  value={inbound.freight_forwarding || ''}
                  onChange={(e) => setInbound({...inbound, freight_forwarding: parseFloat(e.target.value) || 0})}
                  className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-xs text-white"
                  placeholder="$0.00"
                />
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-gray-400 block mb-1">Drayage (Port to Warehouse)</label>
                <input
                  type="number"
                  value={inbound.drayage || ''}
                  onChange={(e) => setInbound({...inbound, drayage: parseFloat(e.target.value) || 0})}
                  className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-xs text-white"
                  placeholder="$0.00"
                />
              </div>
              <div>
                <label className="text-[10px] text-gray-400 block mb-1">Insurance</label>
                <input
                  type="number"
                  value={inbound.insurance || ''}
                  onChange={(e) => setInbound({...inbound, insurance: parseFloat(e.target.value) || 0})}
                  className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-xs text-white"
                  placeholder="$0.00"
                />
              </div>
              <div>
                <label className="text-[10px] text-gray-400 block mb-1">Units Per Container</label>
                <input
                  type="number"
                  value={inbound.units_per_container || ''}
                  onChange={(e) => setInbound({...inbound, units_per_container: parseInt(e.target.value) || 0})}
                  className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-xs text-white"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
          
          {/* Totals */}
          <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-3 gap-4">
            <div className="bg-slate-900 rounded-lg p-3">
              <div className="text-[10px] text-gray-400">Total Inbound Cost</div>
              <div className="text-lg font-bold text-white">${totalInbound.toFixed(2)}</div>
            </div>
            <div className="bg-slate-900 rounded-lg p-3">
              <div className="text-[10px] text-gray-400">Units Per Container</div>
              <div className="text-lg font-bold text-blue-400">{inbound.units_per_container || '—'}</div>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
              <div className="text-[10px] text-emerald-400">Cost Per Unit</div>
              <div className="text-lg font-bold text-emerald-400">${costPerUnit.toFixed(2)}</div>
            </div>
          </div>

          {/* Timeline */}
          <div className="mt-4 pt-4 border-t border-slate-700">
            <div className="text-xs text-gray-400 mb-2">Estimated Timeline</div>
            <div className="flex items-center gap-2 text-[10px]">
              <div className="bg-slate-700 rounded px-2 py-1 text-gray-300">Manufacturing (14d)</div>
              <span className="text-gray-600">→</span>
              <div className="bg-slate-700 rounded px-2 py-1 text-gray-300">Port Loading (3d)</div>
              <span className="text-gray-600">→</span>
              <div className="bg-blue-500/20 text-blue-400 rounded px-2 py-1">Ocean Transit (25-30d)</div>
              <span className="text-gray-600">→</span>
              <div className="bg-slate-700 rounded px-2 py-1 text-gray-300">Customs (3-5d)</div>
              <span className="text-gray-600">→</span>
              <div className="bg-slate-700 rounded px-2 py-1 text-gray-300">Drayage (1-2d)</div>
              <span className="text-gray-600">→</span>
              <div className="bg-green-500/20 text-green-400 rounded px-2 py-1">Receiving (1d)</div>
            </div>
            <div className="mt-2 text-xs text-gray-500">Total: ~47-55 days</div>
          </div>
        </div>

        {/* Outbound Shipping */}
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <Package className="w-4 h-4 text-emerald-400" />
            Outbound Shipping (3PL → Customer)
          </h3>
          
          <table className="w-full text-xs">
            <thead className="bg-slate-900">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-400">Carrier</th>
                <th className="px-3 py-2 text-right text-[10px] uppercase text-gray-400">Avg Cost/Order</th>
                <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-400">Transit Days</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-400">Notes</th>
              </tr>
            </thead>
            <tbody>
              {outboundRates.map((rate, idx) => (
                <tr key={idx} className={`border-b border-slate-700 ${idx % 2 === 0 ? 'bg-slate-800/50' : ''}`}>
                  <td className="px-3 py-2 text-white font-medium">{rate.carrier}</td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      value={rate.cost || ''}
                      onChange={(e) => {
                        const newRates = [...outboundRates];
                        newRates[idx].cost = parseFloat(e.target.value) || 0;
                        setOutboundRates(newRates);
                      }}
                      className="w-20 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white text-right"
                      placeholder="$0.00"
                    />
                  </td>
                  <td className="px-3 py-2 text-center text-gray-300">{rate.transit_days} days</td>
                  <td className="px-3 py-2 text-gray-400">{rate.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============ DASHBOARD TAB ============
  const DashboardTab = () => {
    // Calculate dashboard data
    const providerCosts = providers.map(p => ({
      name: p.provider_name,
      monthly: calculateMonthlyCost(p),
      storage: (p.costs?.storage_per_pallet_monthly || 0) * palletsStored,
      pickPack: (p.costs?.pick_pack_combined || 0) * monthlyOrders,
      shipping: (p.costs?.shipping_per_order_avg || 0) * monthlyOrders
    })).filter(p => p.monthly > 0);

    const lowestCost = Math.min(...providerCosts.map(p => p.monthly).filter(c => c > 0)) || 0;
    const avgCostPerOrder = providerCosts.length > 0 
      ? providerCosts.reduce((sum, p) => sum + p.monthly, 0) / providerCosts.length / monthlyOrders
      : 0;

    // Break-even data
    const breakEvenData = [100, 200, 300, 500, 750, 1000].map(orders => {
      const row = { orders };
      providers.forEach(p => {
        const costs = p.costs || {};
        const storage = (costs.storage_per_pallet_monthly || 0) * palletsStored;
        const pickPack = (costs.pick_pack_combined || 0) * orders;
        const shipping = (costs.shipping_per_order_avg || 0) * orders;
        const minimum = costs.minimum_monthly || 0;
        row[p.provider_name] = Math.max(storage + pickPack + shipping, minimum);
      });
      return row;
    });

    // Scorecard data
    const [scores, setScores] = useState(() => {
      const initial = {};
      providers.forEach(p => {
        initial[p.id] = {
          cost: 0,
          fda: p.fda_compliant ? 10 : 0,
          location: 5,
          tech: 5,
          scalability: 5,
          reputation: 5
        };
      });
      return initial;
    });

    const weights = {
      cost: 0.30,
      fda: 0.20,
      location: 0.15,
      tech: 0.15,
      scalability: 0.10,
      reputation: 0.10
    };

    const calculateTotalScore = (providerId) => {
      const s = scores[providerId] || {};
      return (
        (s.cost || 0) * weights.cost +
        (s.fda || 0) * weights.fda +
        (s.location || 0) * weights.location +
        (s.tech || 0) * weights.tech +
        (s.scalability || 0) * weights.scalability +
        (s.reputation || 0) * weights.reputation
      );
    };

    return (
      <div className="space-y-4">
        {/* KPI Cards */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
            <div className="text-[10px] text-gray-400 uppercase">Lowest Monthly Cost</div>
            <div className="text-xl font-bold text-emerald-400">${lowestCost.toFixed(0)}</div>
          </div>
          <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
            <div className="text-[10px] text-gray-400 uppercase">Avg Cost/Order</div>
            <div className="text-xl font-bold text-white">${avgCostPerOrder.toFixed(2)}</div>
          </div>
          <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
            <div className="text-[10px] text-gray-400 uppercase">Monthly Orders</div>
            <div className="text-xl font-bold text-blue-400">{monthlyOrders}</div>
          </div>
          <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
            <div className="text-[10px] text-gray-400 uppercase">Providers Evaluated</div>
            <div className="text-xl font-bold text-white">{providers.length}</div>
          </div>
        </div>

        {/* Break-Even Chart */}
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <h3 className="text-sm font-semibold text-white mb-4">Break-Even Analysis by Order Volume</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={breakEvenData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="orders" stroke="#9ca3af" fontSize={10} />
              <YAxis stroke="#9ca3af" fontSize={10} tickFormatter={(v) => `$${v}`} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                labelStyle={{ color: '#f8fafc' }}
                formatter={(value) => [`$${value.toFixed(0)}`, '']}
              />
              <Legend />
              {providers.slice(0, 5).map((p, i) => (
                <Line 
                  key={p.id}
                  type="monotone" 
                  dataKey={p.provider_name} 
                  stroke={['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'][i]}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Cost Breakdown Chart */}
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <h3 className="text-sm font-semibold text-white mb-4">Monthly Cost Breakdown by Provider</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={providerCosts}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="name" stroke="#9ca3af" fontSize={10} />
              <YAxis stroke="#9ca3af" fontSize={10} tickFormatter={(v) => `$${v}`} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                formatter={(value) => [`$${value.toFixed(0)}`, '']}
              />
              <Legend />
              <Bar dataKey="storage" stackId="a" fill="#3b82f6" name="Storage" />
              <Bar dataKey="pickPack" stackId="a" fill="#10b981" name="Pick & Pack" />
              <Bar dataKey="shipping" stackId="a" fill="#f59e0b" name="Shipping" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Provider Scorecard */}
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <h3 className="text-sm font-semibold text-white mb-4">Provider Scorecard (1-10 Scale)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-900">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-400">Factor</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-400">Weight</th>
                  {providers.map(p => (
                    <th key={p.id} className="px-3 py-2 text-center text-[10px] uppercase text-gray-400">
                      {p.provider_name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { key: 'cost', label: 'Monthly Cost (est)' },
                  { key: 'fda', label: 'FDA Compliance' },
                  { key: 'location', label: 'Location/Proximity' },
                  { key: 'tech', label: 'Tech/Integrations' },
                  { key: 'scalability', label: 'Scalability' },
                  { key: 'reputation', label: 'Reviews/Reputation' }
                ].map((factor, idx) => (
                  <tr key={factor.key} className={`border-b border-slate-700 ${idx % 2 === 0 ? 'bg-slate-800/50' : ''}`}>
                    <td className="px-3 py-2 text-gray-300">{factor.label}</td>
                    <td className="px-3 py-2 text-center text-gray-400">{(weights[factor.key] * 100).toFixed(0)}%</td>
                    {providers.map(p => (
                      <td key={p.id} className="px-3 py-2 text-center">
                        <input
                          type="number"
                          min="0"
                          max="10"
                          value={scores[p.id]?.[factor.key] || ''}
                          onChange={(e) => {
                            setScores(prev => ({
                              ...prev,
                              [p.id]: {
                                ...prev[p.id],
                                [factor.key]: parseInt(e.target.value) || 0
                              }
                            }));
                          }}
                          className="w-12 bg-slate-700 border border-slate-600 rounded px-1 py-0.5 text-xs text-white text-center"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="bg-slate-900 font-semibold">
                  <td className="px-3 py-2 text-white">TOTAL SCORE</td>
                  <td className="px-3 py-2 text-center text-gray-400">100%</td>
                  {providers.map(p => {
                    const total = calculateTotalScore(p.id);
                    const allTotals = providers.map(pr => calculateTotalScore(pr.id));
                    const isHighest = total > 0 && total === Math.max(...allTotals);
                    
                    return (
                      <td key={p.id} className={`px-3 py-2 text-center ${isHighest ? 'text-green-400' : 'text-white'}`}>
                        {total.toFixed(1)}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-lg">
            <Truck className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">3PL & Operations</h2>
            <p className="text-[10px] text-gray-400">Compare 3PL providers, calculate unit economics, manage shipping</p>
          </div>
        </div>
        
        <button
          onClick={loadData}
          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-white bg-slate-800 rounded border border-slate-700"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-1">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-slate-700'
            }`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'providers' && <ProvidersTab />}
      {activeTab === 'economics' && <UnitEconomicsTab />}
      {activeTab === 'shipping' && <ShippingCalculatorTab />}
      {activeTab === 'planner' && <ShipmentPlannerTab tenantId={tenantId} providers={providers} />}
      {activeTab === 'dashboard' && <DashboardTab />}
    </div>
  );
};

export default TPLModule;
